import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckIcon, ClipboardIcon } from "@heroicons/react/24/outline";

import { TableReportDateWithFormatAndClass } from "../shared-components/TableReportDateWithFormat";

import { usePermissionValidator } from "../api/UserPermissionValidators";
import { ThreeSixtyAmendmentRow } from "../api/ThreeSixty";
import { ThreeSixtyAssignedToCell } from "./ThreeSixtyAssignedToCell";

type ThreeSixtyAmendmentRowProps = {
  row: ThreeSixtyAmendmentRow;
  cancel: string[];
  toggleCancel: (value: string) => number;
  forceReload: () => void;
};

export const ThreeSixtyAmendmentRowRenderer = (
  props: ThreeSixtyAmendmentRowProps
) => {
  const [copied, setCopied] = useState<boolean>(false);
  const location = useLocation();
  const row: ThreeSixtyAmendmentRow = props.row;
  const perms = usePermissionValidator();

  function copyToClipboard(value: string) {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex gap-2 px-2 py-5 border-b text-gray-700">
      <div className="flex-1 flex items-center gap-2 overflow-auto pr-4">
        <div className="flex-1">
          {perms.canViewWIP360Reports || perms.canEdit360Reports ? (
            <Link
              className="underline font-semibold"
              to={`/360/${row.request_uuid}/summary`}
              state={{
                originUrl: `${location.pathname}${location.search}`,
              }}
            >
              <span>{row.name}</span>
            </Link>
          ) : (
            <span className="font-semibold">{row.name}</span>
          )}
          <div
            className="text-xs text-clip whitespace-nowrap mt-1 flex items-center text-gray-700  rounded-md py-1 hover:bg-gray-100 hover:cursor-pointer"
            onClick={() => copyToClipboard(row.request_uuid)}
          >
            {copied ? (
              <CheckIcon className="w-4 inline-block stroke-green-500" />
            ) : (
              <ClipboardIcon className="w-4 inline-block" />
            )}
            <span className="pl-1">{row.request_uuid.slice(0, 8)}</span>
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center">{row.company_name}</div>
      <div className="flex-1">
        <div className="text-md">{row.blueprint_name}</div>
        <div className="text-md mt-2">{row.behavior_kit_name || "General"}</div>
      </div>
      <div className="flex-1 flex gap-2 whitespace-pre text-md items-center">
        {!row.finished_at ? (
          <div>N/A</div>
        ) : (
          <TableReportDateWithFormatAndClass date={row.finished_at ?? ""} />
        )}
      </div>
      <div className="flex-1 flex gap-2 whitespace-pre text-md items-center">
        {!row.amendment_opened_at ? null : (
          <TableReportDateWithFormatAndClass date={row.amendment_opened_at} />
        )}
      </div>
      <div className="flex-1 flex gap-2 whitespace-pre text-md items-center">
        <ThreeSixtyAssignedToCell
          workflowRunUuid={row.workflow_run_uuid}
          lockedBy={row.locked_by}
        />
      </div>
      <div className="flex-1 flex items-center">{row.custom_id}</div>
    </div>
  );
};
